import React, { useState } from 'react';
import { X, Send, MessageSquare } from 'lucide-react';
import { trackEvent, getPostHog } from '../lib/posthog';
import { POSTHOG_HOST } from '../config';

const FeedbackForm = ({ isDarkMode, isOpen, onClose }) => {
  const [feedbackText, setFeedbackText] = useState('');
  const [category, setCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThanks, setShowThanks] = useState(false);

  const getSessionRecordingUrl = () => {
    const posthog = getPostHog();
    if (!posthog) return undefined;

    try {
      const sessionId = posthog?.sessionRecording?.sessionId;
      if (!sessionId) return undefined;

      // Look back 30 seconds before the feedback
      const LOOK_BACK = 30;
      const sessionStartTime = posthog?.sessionManager?._sessionStartTimestamp || 0;
      const recordingStartTime = Math.max(
        Math.floor((new Date().getTime() - sessionStartTime) / 1000) - LOOK_BACK,
        0
      );

      // Use PostHog's config API host as primary, fallback to POSTHOG_HOST, then default
      const apiHost = posthog?.config?.api_host || POSTHOG_HOST || 'https://us.i.posthog.com';
      return `${apiHost}/recordings/${sessionId}?t=${recordingStartTime}`;
    } catch (error) {
      console.error('Error generating session recording URL:', error);
      return undefined;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedbackText.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const posthog = getPostHog();
      const sessionRecordingUrl = getSessionRecordingUrl();
      const sessionId = posthog?.sessionRecording?.sessionId || undefined;

      // Capture the general feedback event
      // Note: User email is automatically captured by PostHog via identifyUser() called on sign-in
      trackEvent('general_feedback_submitted', {
        $feedback: feedbackText.trim(),
        category: category.trim() || undefined,
        sessionRecordingUrl: sessionRecordingUrl,
        $session_id: sessionId,
      });

      // Show success message
      setShowThanks(true);
      setFeedbackText('');
      setCategory('');

      // Close modal after 3 seconds
      setTimeout(() => {
        setShowThanks(false);
        onClose();
      }, 3000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
    setFeedbackText('');
    setCategory('');
    setShowThanks(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999] backdrop-blur-sm"
      onClick={handleCancel}
    >
      <div
        className={`relative max-w-md w-full rounded-lg shadow-xl ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleCancel}
          className={`absolute top-4 right-4 p-1 rounded-md transition-colors ${
            isDarkMode
              ? 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
          }`}
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        {showThanks ? (
          <div className="p-6 text-center">
            <div className={`text-lg font-semibold mb-2 ${
              isDarkMode ? 'text-gray-100' : 'text-gray-900'
            }`}>
              Thank you!
            </div>
            <p className={`text-sm ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Your feedback has been submitted. We appreciate your input! Closing in 3 seconds...
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <MessageSquare className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <h2 className={`text-xl font-semibold ${
                isDarkMode ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Give Feedback
              </h2>
            </div>

            <p className={`text-sm mb-4 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Share your thoughts, feature requests, or general feedback about CivicDuty.
            </p>

            {/* Feedback Text */}
            <div className="mb-4">
              <label
                htmlFor="feedback-text"
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Your Feedback <span className="text-red-500">*</span>
              </label>
              <textarea
                id="feedback-text"
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                placeholder="Share your thoughts, feature requests, or suggestions..."
                required
                rows={5}
                className={`w-full px-4 py-3 rounded-lg border resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>

            {/* Category (Optional) */}
            <div className="mb-6">
              <label
                htmlFor="feedback-category"
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}
              >
                Category <span className="text-xs text-gray-500">(optional)</span>
              </label>
              <select
                id="feedback-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">Select a category...</option>
                <option value="Feature Request">Feature Request</option>
                <option value="General Feedback">General Feedback</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleCancel}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  isDarkMode
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!feedbackText.trim() || isSubmitting}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                  !feedbackText.trim() || isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed text-white'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                <Send className="w-4 h-4" />
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FeedbackForm;
